"use client"

import { useRef, useState, useEffect } from "react"
import Link from "next/link"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

const tabs = [
  { label: "🦊", href: "/" },
  { label: "Digital Pride", href: "/digital-pride" },
  { label: "AI工場", href: "/ai-factory" },
  { label: "サービス", href: "/services" },
  { label: "ニュース", href: "/news" },
  { label: "企業情報", href: "/about" },
]

export function Header() {
  const pathname = usePathname()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([])
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 })

  const activeIndex = tabs.findIndex((tab) => 
    tab.href === "/" ? pathname === "/" : pathname.startsWith(tab.href)
  )

  useEffect(() => {
    // Desktop tabs
    if (activeIndex !== -1 && tabRefs.current[activeIndex]) {
      const activeTab = tabRefs.current[activeIndex]
      if (activeTab) {
        setIndicatorStyle({
          left: activeTab.offsetLeft,
          width: activeTab.offsetWidth,
        })

        // Scroll to center the active tab
        if (scrollContainerRef.current) {
          const container = scrollContainerRef.current
          const tabLeft = activeTab.offsetLeft
          const tabWidth = activeTab.offsetWidth
          const containerWidth = container.offsetWidth
          const scrollLeft = tabLeft - containerWidth / 2 + tabWidth / 2

          container.scrollTo({
            left: scrollLeft,
            behavior: "smooth",
          })
        }
      }
    }
  }, [activeIndex])

  return (
    <header className="sticky top-0 z-50 w-full bg-background">
      {/* モバイル: ロゴのみ */}
      <div className="lg:hidden border-b">
        <div className="container flex h-12 items-center justify-center px-4">
          <Link href="/" className="flex items-center">
            <Image
              src="/images/logos/logo-holizon.png"
              alt="CodeFox"
              width={122}
              height={28}
              className="h-8 w-auto"
              unoptimized
            />
          </Link>
        </div>
      </div>

      {/* デスクトップ: 統合ヘッダー */}
      <div className="hidden lg:block border-b">
        <div className="container flex h-14 items-center px-4">
          <Link href="/" className="mr-8 flex items-center shrink-0">
            <Image
              src="/images/logos/logo-holizon.png"
              alt="CodeFox"
              width={122}
              height={28}
              className="h-8 w-auto"
              unoptimized
            />
          </Link>

          <div
            ref={scrollContainerRef}
            className="flex flex-1 overflow-x-auto scrollbar-hide scroll-smooth relative"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <div className="flex relative">
              {tabs.map((tab, index) => (
                <Link
                  key={tab.href}
                  ref={(el) => {
                    tabRefs.current[index] = el
                  }}
                  href={tab.href}
                  className={cn(
                    "flex items-center px-4 h-14 text-sm font-medium whitespace-nowrap transition-all font-['Noto_Sans_JP',_sans-serif]",
                    "hover:text-[#292623]",
                    (tab.href === "/" ? pathname === "/" : pathname.startsWith(tab.href))
                      ? "text-[#292623]"
                      : "text-muted-foreground",
                    tab.label === "🦊" && !(tab.href === "/" ? pathname === "/" : pathname.startsWith(tab.href))
                      ? "grayscale opacity-60"
                      : ""
                  )}
                >
                  <span className={tab.label === "🦊" ? "text-lg" : ""}>{tab.label}</span>
                </Link>
              ))}
              <div
                className="absolute bottom-0 h-0.5 bg-[#292623] transition-all duration-300 ease-out"
                style={{
                  left: `${indicatorStyle.left}px`,
                  width: `${indicatorStyle.width}px`,
                }}
              />
            </div>
          </div>

          <div className="ml-auto shrink-0">
            <Link href="/contact">
              <Button size="sm" className="bg-[#292623] text-white hover:bg-[#292623]/90 font-['Noto_Sans_JP',_sans-serif]">お問い合わせ</Button>
            </Link>
          </div>
        </div>
      </div>
    </header>
  )
}
